import { useParams, Link } from 'react-router-dom';
import { RefreshCw, PenTool, Sparkles, ArrowRight, Layers } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import api from '@/api';
import { useJobActions } from '@/hooks/useJobActions';
import { JobHeader } from '@/components/JobHeader';
import { PipelineProgressBar } from '@/components/PipelineProgressBar';
import { JobStatsRow } from '@/components/JobStatsRow';
import { JobDataTabs } from '@/components/JobDataTabs';
import { ExportPackageModal } from '@/components/ExportPackageModal';
import { ShareLinkModal } from '@/components/ShareLinkModal';
import ProcessingProgress from '@/components/ProcessingProgress';

export default function JobDetailPage() {
  const { jobId } = useParams();
  const actions = useJobActions(jobId);
  const [batchLabeling, setBatchLabeling] = useState(false);

  const handleBatchLabel = async () => {
    setBatchLabeling(true);
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/batch-auto-label`);
      if (res.data.started) {
        toast.success(`Labeling ${res.data.total_unlabeled} polygons across ${res.data.sheets_count} sheets...`);
      } else {
        toast.info(res.data.message);
        setBatchLabeling(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch labeling failed');
      setBatchLabeling(false);
    }
  };

  if (actions.loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (!actions.job) {
    return <div className="p-10 text-muted-foreground">Job not found</div>;
  }

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="job-detail-page">
      <JobHeader
        job={actions.job}
        jobId={jobId}
        approving={actions.approving}
        onApproveAll={actions.approveAll}
        onExport={actions.handleExport}
        onExportDxf={actions.handleExportDxf}
        onExportPdf={actions.handleExportPdf}
        onExportPackage={actions.handleExportPackage}
        onOpenShareModal={actions.openShareModal}
        retrying={actions.retrying}
        failedPageCount={actions.failedPageCount}
        onRetryFailed={actions.handleRetryFailed}
      />

      <PipelineProgressBar pipelineStage={actions.job.pipeline_stage} />

      {actions.job.status === 'processing' && (
        <div className="mb-6">
          <ProcessingProgress jobId={jobId} onComplete={() => actions.fetchJob()} />
        </div>
      )}

      <JobStatsRow job={actions.job} />

      {/* Drawing Editor CTA - show when extraction is done */}
      {actions.job.status !== 'processing' && actions.job.status !== 'created' && (
        <div className="flex gap-3 mb-6">
          <Link
            to={`/jobs/${jobId}/review`}
            data-testid="drawing-editor-banner"
            className="group flex-1 block p-4 rounded-sm bg-gradient-to-r from-orange-950/30 to-violet-950/20 border border-orange-800/30 hover:border-primary/40/50 transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/15 flex items-center justify-center">
                  <PenTool className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">Drawing Editor</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    View polylines on the floor plan, draw or edit polygons, auto-label with AI, and export
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-heading uppercase tracking-wider text-primary group-hover:text-primary transition-colors">
                  Open Editor
                </span>
                <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
          <button
            onClick={handleBatchLabel}
            disabled={batchLabeling}
            data-testid="batch-auto-label-btn"
            className={`flex flex-col items-center justify-center gap-2 px-6 rounded-sm border transition-all shrink-0 ${
              batchLabeling
                ? 'bg-violet-950/20 border-violet-800/40 text-violet-400 cursor-wait'
                : 'bg-violet-950/10 border-violet-800/30 text-violet-400 hover:border-violet-600/50 hover:bg-violet-950/30'
            }`}
          >
            <Layers className={`w-5 h-5 ${batchLabeling ? 'animate-spin' : ''}`} />
            <span className="text-[10px] font-heading uppercase tracking-wider">
              {batchLabeling ? 'Labeling...' : 'Label All Sheets'}
            </span>
          </button>
        </div>
      )}

      <JobDataTabs job={actions.job} onReview={actions.handleReview} />

      <ExportPackageModal
        show={actions.showPackageModal}
        onClose={() => actions.setShowPackageModal(false)}
        exporting={actions.exportingPackage}
        result={actions.packageResult}
        onDownload={actions.downloadPackage}
      />

      <ShareLinkModal
        show={actions.showShareModal}
        onClose={() => actions.setShowShareModal(false)}
        shareLinks={actions.shareLinks}
        newLink={actions.newLink}
        onNewLinkChange={actions.setNewLink}
        creatingLink={actions.creatingLink}
        createdLink={actions.createdLink}
        copiedCode={actions.copiedCode}
        onCreateLink={actions.createShareLink}
        onRevokeLink={actions.revokeLink}
        onCopyToClipboard={actions.copyToClipboard}
        getPortalUrl={actions.getPortalUrl}
      />
    </div>
  );
}
